import React, { useEffect, useState } from 'react';
import {
    getCountries,
    deleteCountry,
    getStates,
    deleteState,
    getCities,
    deleteCity,
    getLocations,
    deleteLocation,
} from '../../services/locationService';

// Modal Component
function Modal({ isOpen, onClose, onConfirm, title, message }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded shadow-lg w-96">
                <div className="p-4 border-b">
                    <h2 className="text-lg font-semibold">{title}</h2>
                </div>
                <div className="p-4">
                    <p>{message}</p>
                </div>
                <div className="flex justify-end p-4 border-t">
                    <button
                        onClick={onClose}
                        className="mr-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

function LocationList() {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [locations, setLocations] = useState([]);

    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);

    const [loading, setLoading] = useState(true);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({
        title: '',
        message: '',
        onConfirm: () => {},
    });

    useEffect(() => {
        fetchCountries();
    }, []);

    useEffect(() => {
        if (selectedCountry) {
            fetchStates(selectedCountry.country_id);
            setSelectedState(null);
            setCities([]);
            setLocations([]);
            setSelectedCity(null);
            setSelectedLocation(null);
        } else {
            setStates([]);
            setSelectedState(null);
            setCities([]);
            setSelectedCity(null);
            setLocations([]);
            setSelectedLocation(null);
        }
    }, [selectedCountry]);

    useEffect(() => {
        if (selectedState) {
            fetchCities(selectedState.state_id);
            setSelectedCity(null);
            setLocations([]);
            setSelectedLocation(null);
        } else {
            setCities([]);
            setSelectedCity(null);
            setLocations([]);
            setSelectedLocation(null);
        }
    }, [selectedState]);

    useEffect(() => {
        if (selectedCity) {
            fetchLocations(selectedCity.city_id);
            setSelectedLocation(null);
        } else {
            setLocations([]);
            setSelectedLocation(null);
        }
    }, [selectedCity]);

    const fetchCountries = () => {
        getCountries()
            .then((response) => {
                setCountries(response.data);
                setSelectedCountry(response.data[0] || null);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            });
    };

    const fetchStates = (countryId) => {
        getStates(countryId)
            .then((response) => {
                setStates(response.data);
                setSelectedState(response.data[0] || null);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const fetchCities = (stateId) => {
        getCities(stateId)
            .then((response) => {
                setCities(response.data);
                setSelectedCity(response.data[0] || null);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const fetchLocations = (cityId) => {
        getLocations(cityId)
            .then((response) => {
                setLocations(response.data);
                setSelectedLocation(response.data[0] || null);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const handleCountryDelete = (country) => {
        if (!country) return;

        setModalConfig({
            title: 'Delete Country',
            message: `Are you sure you want to delete ${country.name}?`,
            onConfirm: () => {
                deleteCountry(country.country_id)
                    .then(() => {
                        setIsModalOpen(false);
                        fetchCountries();
                    })
                    .catch((error) => {
                        console.error(error);
                        setIsModalOpen(false);
                    });
            },
        });
        setIsModalOpen(true);
    };

    const handleStateDelete = (state) => {
        if (!state) return;

        setModalConfig({
            title: 'Delete State',
            message: `Are you sure you want to delete ${state.name}?`,
            onConfirm: () => {
                deleteState(state.state_id)
                    .then(() => {
                        setIsModalOpen(false);
                        fetchStates(selectedCountry.country_id);
                    })
                    .catch((error) => {
                        console.error(error);
                        setIsModalOpen(false);
                    });
            },
        });
        setIsModalOpen(true);
    };

    const handleCityDelete = (city) => {
        if (!city) return;

        setModalConfig({
            title: 'Delete City',
            message: `Are you sure you want to delete ${city.name}?`,
            onConfirm: () => {
                deleteCity(city.city_id)
                    .then(() => {
                        setIsModalOpen(false);
                        fetchCities(selectedState.state_id);
                    })
                    .catch((error) => {
                        console.error(error);
                        setIsModalOpen(false);
                    });
            },
        });
        setIsModalOpen(true);
    };

    const handleLocationDelete = (location) => {
        if (!location) return;

        setModalConfig({
            title: 'Delete Location',
            message: `Are you sure you want to delete ${location.name}?`,
            onConfirm: () => {
                deleteLocation(location.location_id)
                    .then(() => {
                        setIsModalOpen(false);
                        fetchLocations(selectedCity.city_id);
                    })
                    .catch((error) => {
                        console.error(error);
                        setIsModalOpen(false);
                    });
            },
        });
        setIsModalOpen(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen text-xl">
                Loading...
            </div>
        );
    }

    return (
        <div className="min-h-screen p-8 bg-gray-100">
            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">Locations</h1>
                </div>

                {/* Country Selector */}
                <div className="flex items-center mb-4">
                    <label htmlFor="countries" className="mr-2">Country</label>
                    <select
                        id="countries"
                        className="border border-gray-300 rounded p-1"
                        value={selectedCountry ? selectedCountry.country_id : ''}
                        onChange={(e) =>
                            setSelectedCountry(
                                countries.find(
                                    (country) => country.country_id === parseInt(e.target.value)
                                )
                            )
                        }
                    >
                        {countries.map((country) => (
                            <option key={country.country_id} value={country.country_id}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => handleCountryDelete(selectedCountry)}
                        className="ml-2 bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded"
                    >
                        Delete
                    </button>
                </div>

                {/* State Selector */}
                {states.length > 0 && (
                    <div className="flex items-center mb-4">
                        <label htmlFor="states" className="mr-2">State</label>
                        <select
                            id="states"
                            className="border border-gray-300 rounded p-1"
                            value={selectedState ? selectedState.state_id : ''}
                            onChange={(e) =>
                                setSelectedState(
                                    states.find(
                                        (state) => state.state_id === parseInt(e.target.value)
                                    )
                                )
                            }
                        >
                            {states.map((state) => (
                                <option key={state.state_id} value={state.state_id}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => handleStateDelete(selectedState)}
                            className="ml-2 bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded"
                        >
                            Delete
                        </button>
                    </div>
                )}

                {/* City Selector */}
                {cities.length > 0 && (
                    <div className="flex items-center mb-4">
                        <label htmlFor="cities" className="mr-2">City</label>
                        <select
                            id="cities"
                            className="border border-gray-300 rounded p-1"
                            value={selectedCity ? selectedCity.city_id : ''}
                            onChange={(e) =>
                                setSelectedCity(
                                    cities.find(
                                        (city) => city.city_id === parseInt(e.target.value)
                                    )
                                )
                            }
                        >
                            {cities.map((city) => (
                                <option key={city.city_id} value={city.city_id}>
                                    {city.name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => handleCityDelete(selectedCity)}
                            className="ml-2 bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded"
                        >
                            Delete
                        </button>
                    </div>
                )}

                {/* Location Selector */}
                {locations.length > 0 && (
                    <div className="flex items-center mb-4">
                        <label htmlFor="locations" className="mr-2">Location</label>
                        <select
                            id="locations"
                            className="border border-gray-300 rounded p-1"
                            value={selectedLocation ? selectedLocation.location_id : ''}
                            onChange={(e) =>
                                setSelectedLocation(
                                    locations.find(
                                        (location) => location.location_id === parseInt(e.target.value)
                                    )
                                )
                            }
                        >
                            {locations.map((location) => (
                                <option key={location.location_id} value={location.location_id}>
                                    {location.name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={() => handleLocationDelete(selectedLocation)}
                            className="ml-2 bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded"
                        >
                            Delete
                        </button>
                    </div>
                )}

                {/* Modal */}
                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onConfirm={modalConfig.onConfirm}
                    title={modalConfig.title}
                    message={modalConfig.message}
                />
            </div>
        </div>
    );
}

export default LocationList;
